import ConditionScore from './ConditionScore.jsx';

// Slide-up sheet with full activity detail. Phase 2 keeps it light —
// description, photo, links, condition reasons. Photo waypoints + the
// challenge linking surface here from Phase 3 onwards.
export default function ActivityDetail({ activity, trail, score, onClose }) {
  if (!activity) return null;
  const name = activity.name || trail?.name || activity.id;
  const photo = trail?.profile_photo_url;
  const desc = trail?.curated_description || activity.summary;
  const lengthMi = trail?.length_miles;
  const elevFt = trail?.elevation_gain_feet;
  const difficulty = trail?.difficulty;
  const rating = trail?.avg_rating;
  const url = trail?.alltrails_web_url;

  return (
    <div className="fixed inset-0 z-40 flex items-end justify-center bg-black/40" onClick={onClose}>
      <div
        className="card w-full max-w-screen-sm overflow-hidden rounded-b-none bg-bg-primary"
        style={{ paddingBottom: 'var(--safe-bottom)' }}
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between border-b border-border-tertiary px-4 py-3">
          <h2 className="truncate font-medium">{name}</h2>
          <button
            type="button"
            onClick={onClose}
            aria-label="Close"
            className="rounded-md p-1 text-fg-tertiary hover:bg-bg-tertiary hover:text-fg-primary"
          >
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round">
              <path d="M6 6l12 12M18 6L6 18" />
            </svg>
          </button>
        </div>

        <div className="max-h-[70vh] overflow-y-auto">
          {photo && (
            <img
              src={photo}
              alt=""
              className="h-44 w-full object-cover"
              loading="lazy"
              referrerPolicy="no-referrer"
            />
          )}

          <div className="space-y-3 p-4">
            {Number.isFinite(score?.score) && (
              <div className="flex items-center justify-between">
                <ConditionScore score={score.score} label="now" />
                <span className="font-mono text-[10px] uppercase tracking-wider text-fg-tertiary">
                  conditions
                </span>
              </div>
            )}

            <div className="flex flex-wrap gap-x-4 gap-y-1 font-mono text-xs text-fg-secondary">
              {Number.isFinite(lengthMi) && <span>{lengthMi.toFixed(1)} mi</span>}
              {Number.isFinite(elevFt) && <span>↑ {elevFt} ft</span>}
              {difficulty && <span>{difficulty}</span>}
              {Number.isFinite(rating) && <span>★ {rating.toFixed(1)}</span>}
              {trail?.route_type && <span>{trail.route_type}</span>}
            </div>

            {desc && <p className="text-sm text-fg-primary">{desc}</p>}

            {score?.reasons?.length > 0 && (
              <ul className="space-y-1 rounded-md bg-bg-secondary p-3 text-xs text-fg-secondary">
                {score.reasons.map((r) => (
                  <li key={r}>· {r}</li>
                ))}
              </ul>
            )}

            {trail?.review_summary && (
              <div>
                <p className="font-mono text-[10px] uppercase tracking-wider text-fg-tertiary">
                  AllTrails reviews
                </p>
                <p className="mt-1 text-xs text-fg-secondary">{trail.review_summary}</p>
              </div>
            )}

            {(activity.subjects || activity.features || trail?.features) && (
              <FeatureChips
                items={Array.from(
                  new Set([
                    ...(activity.subjects || []),
                    ...(activity.features || []),
                    ...(trail?.features || []),
                  ])
                )}
              />
            )}

            <div className="flex flex-wrap gap-2 pt-1">
              {url && (
                <a
                  href={url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="rounded-md border border-border-tertiary bg-bg-secondary px-3 py-2 font-mono text-xs uppercase tracking-wider text-action transition hover:bg-bg-tertiary"
                >
                  Open in AllTrails
                </a>
              )}
              {(activity.lat || trail?.trailhead?.lat) && (
                <a
                  href={`https://www.google.com/maps/dir/?api=1&destination=${activity.lat ?? trail.trailhead.lat},${activity.lng ?? trail.trailhead.lng}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="rounded-md border border-border-tertiary bg-bg-secondary px-3 py-2 font-mono text-xs uppercase tracking-wider text-action transition hover:bg-bg-tertiary"
                >
                  Directions
                </a>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

function FeatureChips({ items }) {
  if (!items?.length) return null;
  return (
    <div className="flex flex-wrap gap-1.5">
      {items.slice(0, 14).map((it) => (
        <span
          key={it}
          className="rounded-full border border-border-tertiary bg-bg-secondary px-2 py-0.5 text-[11px] text-fg-secondary"
        >
          {String(it).replace(/_/g, ' ')}
        </span>
      ))}
    </div>
  );
}
